'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleFilled } from '@ant-design/icons';
import { Button as AntdButton, Input, InputRef, Modal, QRCode, message } from 'antd';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import CopyText from '@/components/copy';
import { TwoFAActiveIcon } from '@/app/const/svg';

export type TwoFaModalMode = 'bind' | 'verify';

export interface TwoFaModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** 绑定模式：扫码+密钥+6位码；验证模式：仅安全验证+6位码 */
  mode: TwoFaModalMode;
  /** 验证模式下点击「无法验证？提交反馈」的回调 */
  onFeedbackClick?: () => void;
  /** 绑定成功后是否展示成功态弹窗（默认 true）；为 false 时直接 onSuccess + 关闭 */
  showSuccessScreen?: boolean;
}

const TWO_FA_FEEDBACK_PAYLOAD = {
  problemType: '2',
  problemDescription: '该用户无法验证2FA，需要协助',
  problemPage: '2fa_verification',
  contactEmail: 'noreply@xmarslive.com',
  screenshotUrl: '',
};

export default function TwoFaModal({ open, mode, onClose, onSuccess, onFeedbackClick, showSuccessScreen = true }: TwoFaModalProps) {
  const { t } = useTranslation();
  const [twoFaSecretKey, setTwoFaSecretKey] = useState('');
  const [twoFaQrCodeUri, setTwoFaQrCodeUri] = useState('');
  const [twoFaVerifyDigits, setTwoFaVerifyDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [twoFaInitLoading, setTwoFaInitLoading] = useState(false);
  const [twoFaConfirmLoading, setTwoFaConfirmLoading] = useState(false);
  const [twoFaSuccessShow, setTwoFaSuccessShow] = useState(false);
  const [twoFaSecretKeyExpireAt, setTwoFaSecretKeyExpireAt] = useState<number | null>(null);
  const [twoFaCountdownSeconds, setTwoFaCountdownSeconds] = useState<number | null>(null);
  const [twoFaVerifyError, setTwoFaVerifyError] = useState(false);
  const [twoFaFeedbackSubmitting, setTwoFaFeedbackSubmitting] = useState(false);
  const firstDigitInputRef = useRef<HTMLInputElement>(null);
  const isVerifyMode = mode === 'verify';

  const focusFirstDigitInput = () => {
    setTimeout(() => (firstDigitInputRef.current as HTMLInputElement)?.focus(), 0);
  };
  const fetchTwoFaBindInit = () => {
    setTwoFaInitLoading(true);
    return api.user
      .getTwoFaBindInit()
      .then(res => {
        if (res?.code === 200) {
          if (res?.data?.secretKey) setTwoFaSecretKey(res.data.secretKey);
          if (res?.data?.qrCodeUri) setTwoFaQrCodeUri(res.data.qrCodeUri);
          if (res?.data?.secretKeyExpireAt != null) {
            setTwoFaSecretKeyExpireAt(res.data.secretKeyExpireAt);
          } else {
            setTwoFaSecretKeyExpireAt(null);
          }
        } else {
          message.error(res?.msg || t('me.twoFaInitFailed'));
        }
      })
      .catch(() => message.error(t('me.twoFaInitFailed')))
      .finally(() => {
        setTwoFaInitLoading(false);
        focusFirstDigitInput();
      });
  };

  useEffect(() => {
    if (!open) return;
    setTwoFaSuccessShow(false);
    setTwoFaVerifyDigits(['', '', '', '', '', '']);
    setTwoFaVerifyError(false);
    if (!isVerifyMode) {
      setTwoFaSecretKey('');
      setTwoFaQrCodeUri('');
      setTwoFaSecretKeyExpireAt(null);
      setTwoFaCountdownSeconds(null);
      fetchTwoFaBindInit();
    } else {
      focusFirstDigitInput();
    }
  }, [open, isVerifyMode]);

  useEffect(() => {
    if (isVerifyMode || !open || twoFaSuccessShow || twoFaSecretKeyExpireAt == null) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((twoFaSecretKeyExpireAt - Date.now()) / 1000));
      setTwoFaCountdownSeconds(remaining);
      if (remaining <= 0) {
        setTwoFaSecretKeyExpireAt(null);
        setTwoFaCountdownSeconds(0);
        fetchTwoFaBindInit();
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [open, twoFaSuccessShow, twoFaSecretKeyExpireAt]);

  const handleTwoFaDigitChange = (index: number, value: string) => {
    if (twoFaVerifyError) setTwoFaVerifyError(false);
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const next = [...twoFaVerifyDigits];
    next[index] = value;
    setTwoFaVerifyDigits(next);
    if (value && index < 5) {
      const el = document.getElementById(`twofa-digit-${index + 1}`);
      (el as HTMLInputElement)?.focus();
    }
    if (value && next.join('').length === 6) {
      handleTwoFaConfirm(next.join(''));
    }
  };

  const handleTwoFaDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return;
    if (twoFaVerifyDigits[index]) return;
    if (index > 0) {
      e.preventDefault();
      const next = [...twoFaVerifyDigits];
      next[index - 1] = '';
      setTwoFaVerifyDigits(next);
      const el = document.getElementById(`twofa-digit-${index - 1}`);
      (el as HTMLInputElement)?.focus();
    }
  };

  const handleTwoFaPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);
    if (digits.length > 0) {
      const next = [...twoFaVerifyDigits];
      for (let i = 0; i < digits.length; i++) {
        next[i] = digits[i];
      }
      setTwoFaVerifyDigits(next);

      if (twoFaVerifyError) setTwoFaVerifyError(false);

      if (digits.length === 6) {
        setTimeout(() => handleTwoFaConfirm(next.join('')), 0);
      } else {
        const focusIndex = Math.min(digits.length, 5);
        const el = document.getElementById(`twofa-digit-${focusIndex}`);
        (el as HTMLInputElement)?.focus();
      }
    }
  };

  // 确认提交验证码
  const handleTwoFaConfirm = (overrideCode?: string) => {
    const code = overrideCode ?? twoFaVerifyDigits.join('');
    if (code.length !== 6) {
      message.warning(t('me.twoFaEnterCode'));
      return;
    }
    setTwoFaConfirmLoading(true);
    if (isVerifyMode) {
      api.user
        .postTwoFaVerify(code)
        .then(res => {
          if (res?.code === 200) {
            setTwoFaVerifyError(false);
            onSuccess?.();
            onClose();
          } else if (res?.code === 2002) {
            message.error(t('me.twoFaInvalidCode'));
          } else {
            setTwoFaVerifyError(true);
            message.error(res?.msg || t('me.twoFaCodeErrorOrExpired'));
          }
        })
        .catch((err: any) => {
          setTwoFaVerifyError(true);
          message.error(err?.msg || err?.message || t('me.twoFaCodeErrorOrExpired'));
        })
        .finally(() => setTwoFaConfirmLoading(false));
      return;
    }
    api.user
      .postTwoFaBindConfirm(code)
      .then(res => {
        if (res?.code === 200) {
          setTwoFaVerifyError(false);
          if (showSuccessScreen) {
            setTwoFaSuccessShow(true);
            onSuccess?.();
          } else {
            onSuccess?.();
            onClose();
          }
        } else {
          setTwoFaVerifyError(true);
          if (res?.code === 2008) {
            message.error(t('me.twoFaQrExpired'));
          } else if (res?.code === 2004) {
            message.error(t('me.twoFaCodeErrorOrExpired'));
          } else {
            message.error(res?.msg || t('me.twoFaBindFailed'));
          }
        }
      })
      .catch((err: any) => {
        setTwoFaVerifyError(true);
        message.error(err?.msg || err?.message || t('me.twoFaBindFailed'));
      })
      .finally(() => setTwoFaConfirmLoading(false));
  };

  const handleClose = () => {
    onClose();
  };

  // 提交反馈
  const handleTwoFaFeedbackSubmit = async () => {
    onFeedbackClick?.();
    setTwoFaFeedbackSubmitting(true);
    try {
      const res = await api.user.submitFeedback(TWO_FA_FEEDBACK_PAYLOAD);
      if (res?.code === 200) {
        message.success(t('me.twoFaFeedbackSubmitted'));
      } else {
        message.error(res?.msg || t('me.twoFaFeedbackSubmitFailed'));
      }
    } catch (e: any) {
      message.error(e?.message || t('me.twoFaFeedbackSubmitFailed'));
    } finally {
      setTwoFaFeedbackSubmitting(false);
    }
  };

  // 复用的 6 位验证码输入框（绑定/验证模式共用）
  const renderSixDigitInputs = () => (
    <div className="flex w-full justify-between">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <Input
          key={i}
          ref={i === 0 ? (firstDigitInputRef as React.Ref<InputRef>) : undefined}
          id={i === 0 ? 'twofa-digit-0' : `twofa-digit-${i}`}
          maxLength={1}
          value={twoFaVerifyDigits[i]}
          onChange={e => handleTwoFaDigitChange(i, e.target.value)}
          onKeyDown={e => handleTwoFaDigitKeyDown(i, e)}
          onPaste={handleTwoFaPaste}
          autoComplete="off"
          status={twoFaVerifyError ? 'error' : undefined}
          className="w-[50px] h-[50px] text-center text-lg border-button-border text-font-primary focus:border-green-500 [&_.ant-input]:!text-center [&_.ant-input]:!h-full"
        />
      ))}
    </div>
  );

  const modalTitle = twoFaSuccessShow ? null : isVerifyMode ? t('me.twoFaVerifyTitle') : t('me.twoFaSetupTitle');

  return (
    <Modal
      centered
      title={modalTitle ? <div className="text-xl font-semibold text-font-primary text-center">{modalTitle}</div> : null}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={500}
      maskClosable={false}
      closable={!twoFaSuccessShow}
    >
      {isVerifyMode ? (
        <div className="flex flex-col items-center py-2">
          <TwoFAActiveIcon className="w-20 h-20 mb-6" />
          <p className="text-font-primary text-sm mb-6">{t('me.twoFaVerifyHint')}</p>
          <div className="w-full mb-6">{renderSixDigitInputs()}</div>
          <div className="flex gap-3 w-full mb-4">
            <AntdButton size="large" className="flex-1 rounded-lg border-button-border text-font-primary" onClick={handleClose}>
              {t('me.twoFaCancel')}
            </AntdButton>
            <AntdButton
              type="primary"
              size="large"
              loading={twoFaConfirmLoading}
              onClick={() => handleTwoFaConfirm()}
              className="flex-1 !bg-green-500 hover:!bg-green-600 !border-0 rounded-lg"
            >
              {t('me.twoFaConfirm')}
            </AntdButton>
          </div>
          <div
            onClick={() => !twoFaFeedbackSubmitting && handleTwoFaFeedbackSubmit()}
            className="text-amber-500 text-xs hover:text-amber-400 cursor-pointer mt-2 underline"
          >
            {t('me.twoFaCantVerifyFeedback')}
          </div>
        </div>
      ) : twoFaSuccessShow ? (
        <div className="flex flex-col items-center ">
          <CheckCircleFilled className="text-primary mt-6" style={{ fontSize: 36 }} />
          <p className="text-font-primary text-lg mb-8 mt-4">{t('me.twoFaEnabledSuccessfully')}</p>
          <AntdButton
            type="primary"
            size="large"
            onClick={handleClose}
            className="!bg-green-500 hover:!bg-green-600 !border-0 min-w-[120px] rounded-lg w-full"
          >
            {t('me.twoFaSuccessOk')}
          </AntdButton>
        </div>
      ) : (
        <>
          <p className="text-font-primary text-sm mb-4">{t('me.twoFaSetupDesc')}</p>
          <div className="flex justify-center mb-4">
            {twoFaInitLoading ? (
              <div className="w-[200px] h-[200px] bg-background-3/10 rounded flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-font-primary/50" />
              </div>
            ) : twoFaQrCodeUri ? (
              <div className="flex flex-col items-center">
                <div className="rounded bg-white">
                  <QRCode value={twoFaQrCodeUri} size={200} color="#000000" bgColor="#ffffff" />
                </div>
                {twoFaSecretKeyExpireAt != null && (
                  <p className="text-font-primary/70 text-xs mt-2 w-full text-center">
                    {t('me.twoFaQrExpireCountdown', {
                      time: (() => {
                        const sec = twoFaCountdownSeconds ?? Math.max(0, Math.floor((twoFaSecretKeyExpireAt - Date.now()) / 1000));
                        return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
                      })(),
                    })}
                  </p>
                )}
              </div>
            ) : null}
          </div>
          <p className="text-amber-500 text-xs mb-4 text-center">{t('me.twoFaCantScan')}</p>
          <div className="mb-4">
            <label className="text-font-primary text-sm block mb-2">{t('me.twoFaSecretKey')}</label>
            <Input
              readOnly
              value={twoFaSecretKey ? twoFaSecretKey.replace(/(.{4})/g, '$1 ').trim() : ''}
              className="border-button-border text-font-primary"
              suffix={twoFaSecretKey ? <CopyText showText={false} text={twoFaSecretKey} className="text-xs" svgClassName="!w-2.5 !h-2.5" /> : null}
            />
            <div className="mb-6 mt-3">
              <label className="text-font-primary text-sm block">{t('me.twoFaEnterVerificationCode')}</label>
              <p className="text-font-gray text-xs mt-2 mb-3">{t('me.twoFaEnterCodeHint')}</p>
              {renderSixDigitInputs()}
            </div>
            <AntdButton
              type="primary"
              block
              size="large"
              loading={twoFaConfirmLoading}
              onClick={() => handleTwoFaConfirm()}
              className="!bg-green-500 hover:!bg-green-600 !border-0 h-11"
            >
              {t('me.twoFaConfirmSetup')}
            </AntdButton>
          </div>
        </>
      )}
    </Modal>
  );
}
